// Material thumbnail: a small real-render snapshot of a material, captured when
// the material graph editor commits, stored on the material's `data.thumbnail`
// (a downscaled data-image URL) and shown on the content-browser card.
//
// Why: the card's fallback swatch (AssetIcon → MaterialSphere) is a CSS-only
// fake that grabs "any TextureSample texture" and ignores graph connectivity,
// so it diverges from what the material actually renders. Capturing the editor
// preview's real WebGL output once per commit keeps the card faithful for ANY
// graph (fresnel/noise/lerp), at zero per-card cost (the card renders an <img>).
//
// The capturer is registered by the live MaterialPreview (it owns the WebGL
// canvas) and invoked by MaterialGraphModal at commit time — a tiny indirection
// so the modal doesn't have to thread a ref through three component layers.

// The currently-mounted preview's capture function, or null. Module-level
// singleton: there is only ever one material graph editor open at a time.
let _capturer = null

// Register the active capturer (called by the mounted MaterialPreview with a
// fn that reads its WebGL canvas → downscaled data URL). Returns an unregister
// that only clears if THIS fn is still active, so a stale unmount cleanup can't
// wipe a newer preview's capturer.
export function setThumbnailCapturer(fn) {
  _capturer = fn
  return () => { if (_capturer === fn) _capturer = null }
}

// Capture a thumbnail from the live preview, or null if none is mounted or the
// capture fails (compile error, lost GL context). Never throws — a failed capture
// must not block committing the graph edit.
//
// ASYNC because the capture is a GPU readback. The node renderer has no
// `preserveDrawingBuffer`, so the preview cannot be read off its canvas; it
// renders into a target and reads that back, which is a round trip. Callers get a
// promise and should treat a rejection the same as a null — hence the catch here
// covering both the synchronous throw and the rejected promise.
export async function captureMaterialThumbnail() {
  if (!_capturer) return null
  try {
    return (await _capturer()) ?? null
  } catch {
    return null
  }
}

// The card thumbnail source for a material asset: its stored render snapshot if
// present and usable, else null (the card falls back to the heuristic sphere).
// Guards against non-strings and non-image junk so a bad value never renders a
// broken <img>.
export function materialThumbnailSrc(asset) {
  const t = asset?.data?.thumbnail
  return typeof t === 'string' && t.startsWith('data:image') ? t : null
}
